/**
 * Shared form handling utilities for group management forms
 */
import { ALLOWED_LEVERAGE, CATEGORY_LEVERAGE_LIMITS, isAllowedLeverage, findClosestAllowedLeverage, getMaxAllowedLeverageForCategory } from './constants';

/**
 * Handle input change for form fields
 * @param {Function} setFormData - Form data setter function
 * @param {Function} setFormErrors - Form errors setter function
 * @param {string} field - Field name
 * @param {any} value - New value
 */
export const handleInputChange = (setFormData, setFormErrors, field, value) => {
  setFormData(prev => ({
    ...prev,
    [field]: value
  }));
  
  // Clear error for this field when user starts typing
  if (setFormErrors) {
    setFormErrors(prev => ({
      ...prev,
      [field]: ''
    }));
  }
};


/**
 * Handle category leverage settings change
 * @param {Function} setFormData - Form data setter function
 * @param {Function} setFormErrors - Form errors setter function
 * @param {string} category - Leverage category (forex, stocks, etc.)
 * @param {string} field - Field name (maxLeverage, currentLeverage)
 * @param {number} value - New value
 */
export const handleLeverageChange = (setFormData, setFormErrors, category, field, value) => {
  const newValue = parseFloat(value) || 0;
  
  setFormData(prev => {
    let updatedCategorySettings = {
      ...prev.leverageSettings[category],
      [field]: newValue
    };
    
    // Apply validation rules for leverage values
    if (field === 'maxLeverage') {
      // Must be positive integer
      if (newValue <= 0 || !Number.isInteger(newValue)) {
        updatedCategorySettings.maxLeverage = 1; // Default to 1 if invalid
      } else {
        // Must be in allowed list
        if (!isAllowedLeverage(newValue)) {
          // Find closest allowed value
          updatedCategorySettings.maxLeverage = findClosestAllowedLeverage(newValue);
        } else {
          updatedCategorySettings.maxLeverage = newValue;
        }
      }
      
      // Must not exceed category max
      const categoryMax = getMaxAllowedLeverageForCategory(category);
      if (updatedCategorySettings.maxLeverage > categoryMax) {
        updatedCategorySettings.maxLeverage = categoryMax;
      }
      
      // If current leverage exceeds new max, adjust it
      if (updatedCategorySettings.currentLeverage > updatedCategorySettings.maxLeverage) {
        updatedCategorySettings.currentLeverage = updatedCategorySettings.maxLeverage;
      }
    } else if (field === 'currentLeverage') {
      // Must be positive integer
      if (newValue <= 0 || !Number.isInteger(newValue)) {
        updatedCategorySettings.currentLeverage = 1; // Default to 1 if invalid
      } else {
        // Must be in allowed list
        if (!isAllowedLeverage(newValue)) {
          // Find closest allowed value
          updatedCategorySettings.currentLeverage = findClosestAllowedLeverage(newValue);
        } else {
          updatedCategorySettings.currentLeverage = newValue;
        }
      }
      
      // Must not exceed max leverage
      const categoryMax = prev.leverageSettings[category]?.maxLeverage || getMaxAllowedLeverageForCategory(category);
      if (updatedCategorySettings.currentLeverage > categoryMax) {
        updatedCategorySettings.currentLeverage = categoryMax;
      }
    }
    
    const updatedFormData = {
      ...prev,
      leverageSettings: {
        ...prev.leverageSettings,
        [category]: updatedCategorySettings
      }
    };
    
    // Clear the category-specific error if it's resolved
    if (setFormErrors) {
      setFormErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        const errorKey = `leverage_${category}_${field}`;
        delete newErrors[errorKey];
        return newErrors;
      });
    }
    
    return updatedFormData;
  });
};

/**
 * Handle blur event for form fields
 * @param {Function} setTouched - Touched fields setter function
 * @param {string} field - Field name
 */
export const handleBlur = (setTouched, field) => {
  setTouched(prev => ({
    ...prev,
    [field]: true
  }));
};

/**
 * Handle value click for inline editing
 * @param {Object} formData - Current form data
 * @param {Function} setEditingValue - Editing value setter function
 * @param {Function} setTempValue - Temporary value setter function
 * @param {string} field - Field name
 * @param {string|null} category - Category name (for leverage settings)
 * @param {string|null} subField - Sub-field name (for leverage settings)
 */
export const handleValueClick = (formData, setEditingValue, setTempValue, field, category = null, subField = null) => {
  let currentValue;
  if (category && subField) {
    currentValue = formData.leverageSettings?.[category]?.[subField] || 0;
  } else {
    currentValue = formData[field] ?? 0;
  }
  
  setEditingValue({ field, category, subField });
  setTempValue(currentValue.toString());
};

/**
 * Handle value change for inline editing
 * @param {Function} setTempValue - Temporary value setter function
 * @param {string} value - New value
 */
export const handleValueChange = (setTempValue, value) => {
  setTempValue(value);
};

/**
 * Handle value submit for inline editing
 * @param {Object} editingValue - Current editing value object
 * @param {string} tempValue - Temporary value
 * @param {Function} handleInputChange - Input change handler
 * @param {Function} handleLeverageChange - Leverage change handler
 * @param {Function} setEditingValue - Editing value setter function
 * @param {Function} setTempValue - Temporary value setter function
 */
export const handleValueSubmit = (
  editingValue,
  tempValue,
  handleInputChange,
  handleLeverageChange,
  setEditingValue,
  setTempValue
) => {
  if (editingValue) {
    const { field, category, subField } = editingValue;
    let parsedValue = parseFloat(tempValue) || 0;
    
    // If the tempValue is empty or invalid, use the current form value
    if (isNaN(parsedValue)) {
      if (category && subField) {
        // This would need access to formData, but we'll handle it in the component
        return; // Let the component handle this case
      }
    }
    
    if (category && subField) {
      handleLeverageChange(category, subField, parsedValue);
    } else {
      handleInputChange(field, parsedValue);
    }
    
    setEditingValue(null);
    setTempValue('');
  }
};

/**
 * Handle value cancel for inline editing
 * @param {Function} setEditingValue - Editing value setter function
 * @param {Function} setTempValue - Temporary value setter function
 */
export const handleValueCancel = (setEditingValue, setTempValue) => {
  setEditingValue(null);
  setTempValue('');
};

/**
 * Handle key press for inline editing
 * @param {Event} e - Keyboard event
 * @param {Function} handleValueSubmit - Value submit handler
 * @param {Function} handleValueCancel - Value cancel handler
 */
export const handleValueKeyPress = (e, handleValueSubmit, handleValueCancel) => {
  if (e.key === 'Enter') {
    handleValueSubmit();
  } else if (e.key === 'Escape') {
    handleValueCancel();
  }
};
